// Lushin Discovery Call Rubric dimensions — the 10 categories scored by the
// post-call analyzer (1-5 each). Single source of truth for the admin-portal
// dashboard's dimension axes/columns. Keep in sync with
// services/utils/rubric/discovery_call_rubric.py and the rollup/backend-tenant/
// superadmin-portal copies.

export interface RubricDimension {
  key: string;
  label: string;
  // Short label for compact chart axes / table headers.
  short: string;
}

export const RUBRIC_DIMENSIONS: RubricDimension[] = [
  { key: 'up_front_contract_posture', label: 'Up-Front Contract & Posture', short: 'UFC/Posture' },
  { key: 'opening_question_vs_story', label: 'Opening — Question vs. Story', short: 'Opening' },
  { key: 'pain_discovery_levels', label: 'Pain Discovery', short: 'Pain' },
  { key: 'pain_with_price_tag', label: 'Pain With a Price Tag', short: 'Pain $' },
  { key: 'things_prospect_volunteered', label: 'Things the Prospect Volunteered', short: 'Volunteered' },
  { key: 'negative_reverse_reversing', label: 'Negative Reverse / Reversing', short: 'Neg. Reverse' },
  { key: 'equal_business_stature', label: 'Equal Business Stature', short: 'EBS' },
  { key: 'budget_decision_process', label: 'Budget & Decision Process', short: 'Budget' },
  { key: 'up_front_contract_close', label: 'Up-Front Contract for the Close', short: 'Close UFC' },
  { key: 'coachs_discipline', label: "Coach's Discipline", short: 'Discipline' }
];

export const RUBRIC_DIMENSION_KEYS: string[] = RUBRIC_DIMENSIONS.map((d) => d.key);

// The rubric's native scale: each category is scored 1-5.
export const RUBRIC_MAX_SCORE = 5;

// --- Per-session weighting -------------------------------------------------
// Admins retune these per call from the Call Insights tab. Keep in sync with
// services/utils/rubric/discovery_call_rubric.py and
// backend/src/constants/rubricDimensions.js.

// Weights always sum to this. Ten dimensions at 1.0 each = equal weighting.
export const WEIGHT_TOTAL = 10;
export const MIN_WEIGHT = 0;
export const MAX_WEIGHT = 3;
// Editor granularity. All weights are multiples of this.
export const WEIGHT_STEP = 0.1;
// Float-sum comparisons must tolerate accumulated 0.1-step error.
export const WEIGHT_SUM_EPSILON = 0.001;

// Drained LAST when the admin raises another dimension: the two Up-Front
// Contract dimensions (the meeting frame and the binary next step) and both
// Pain dimensions. Everything else absorbs the change first. Redistribution
// order only — this does not change how any dimension is graded.
export const PROTECTED_DIMENSION_KEYS: string[] = [
  'up_front_contract_posture',
  'pain_discovery_levels',
  'pain_with_price_tag',
  'up_front_contract_close'
];

// Equal-weight baseline every session starts from.
export const EQUAL_WEIGHTS: Record<string, number> = RUBRIC_DIMENSION_KEYS.reduce(
  (acc, key) => ({ ...acc, [key]: WEIGHT_TOTAL / RUBRIC_DIMENSION_KEYS.length }),
  {}
);

// Admin-authored instruction appended to every dimension's grading prompt.
export const MAX_CUSTOM_INSTRUCTION_LENGTH = 2000;

// Band color tokens (match styles/lushin-tokens.css --band-*).
export const BAND_COLORS: Record<string, string> = {
  standard: '#00E5C7',
  strong: '#4ADE80',
  soft: '#FACC15',
  at_risk: '#FB923C',
  lost: '#F87171'
};
